// src/services/questionFactory/providers/claudeProvider.js
//
// Claude (Anthropic) generation provider. Phase 2b — wired live.
// Talks to the shared question-factory-generate edge function which
// dispatches by `provider: 'claude'` and holds ANTHROPIC_API_KEY
// server-side.
//
// review() (multi-AI adjudication) wires up in Phase 2c.

import { supabase } from '../../supabaseClient';
import { ProviderError, normalizeGenerateOpts } from './providerBase';

const PROVIDER_ID   = 'claude';
const DISPLAY_NAME  = 'Claude';
const DEFAULT_MODEL = 'claude-sonnet-4-5';

async function generate(opts = {}) {
  if (!opts.runId) {
    throw new ProviderError('generate(): runId is required (create a pipeline run first)', { provider: PROVIDER_ID });
  }
  const o = normalizeGenerateOpts(opts);

  const payload = {
    run_id:             opts.runId,
    provider:           PROVIDER_ID,
    exam_id:            o.examId,
    count:              o.count,
    domain:             o.domain,
    model:              opts.model || DEFAULT_MODEL,
    difficulty:         opts.difficulty || null,
    extra_instructions: opts.extraInstructions || null,
  };

  const { data, error } = await supabase.functions.invoke('question-factory-generate', { body: payload });
  if (error) {
    let detail = error.message || String(error);
    try {
      if (error.context && typeof error.context.text === 'function') {
        const txt = await error.context.text();
        if (txt) detail = `${detail} — ${txt}`;
      }
    } catch { /* swallow */ }
    throw new ProviderError(`Claude generate failed: ${detail}`, { provider: PROVIDER_ID, cause: error });
  }
  if (!data)        throw new ProviderError('Claude generate: empty response', { provider: PROVIDER_ID });
  if (data.error)   throw new ProviderError(`Claude generate: ${data.error}`,   { provider: PROVIDER_ID });

  return {
    inserted:     Number(data.items_inserted || 0),
    failed:       Number(data.items_failed   || 0),
    modelUsed:    data.model_used || DEFAULT_MODEL,
    inputTokens:     Number(data.input_tokens  || 0),
    outputTokens:    Number(data.output_tokens || 0),
    estimatedCostUsd: Number(data.estimated_cost_usd || 0),
    warnings:        Array.isArray(data.warnings) ? data.warnings : [],
    items:           Array.isArray(data.items) ? data.items : [],
  };
}

async function review(opts = {}) {
  if (!opts.question) {
    throw new ProviderError('review(): question is required', { provider: PROVIDER_ID });
  }
  throw new ProviderError('Claude review() is implemented in Question Factory Phase 2c.', { provider: PROVIDER_ID });
}

const claudeProviderModule = {
  id:             PROVIDER_ID,
  displayName:    DISPLAY_NAME,
  enabled:        true,
  supportedModes: ['generate'],   // review() added in Phase 2c
  defaultModel:   DEFAULT_MODEL,
  generate,
  review,
};

export default claudeProviderModule;
